import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  XMarkIcon, 
  ClockIcon, 
  CheckCircleIcon, 
  ExclamationTriangleIcon,
  CalendarIcon,
  UserIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { TaskService, formatTaskFromApi } from '../../services/TaskService';
import EditTaskModal from '../tasks/EditTaskModal';
import { getStatusLabel, getStatusColorClasses, getStatusConfig } from '../../utils/statusConfig';
import toast from 'react-hot-toast';
import { formatDateForAPI, isEndDateAfterStartDate, getMaxDateString } from '../../utils/dateUtils';

const ProjectTasksModal = ({ isOpen, onClose, project, onTasksUpdated, refreshSignal = 0 }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [newTask, setNewTask] = useState({
    name: '',
    description: '',
    initialDate: '',
    finalDate: ''
  });
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    task: null,
    loading: false
  });

  // Cargar tareas cuando se abre el modal
  useEffect(() => {
    if (isOpen && project?.id) {
      loadTasks();
    }
  }, [isOpen, project?.id, refreshSignal]);

  const loadTasks = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const result = await TaskService.getProjectTasks(project.id);
      
      if (result.success) {
        const formattedTasks = result.tasks.map(formatTaskFromApi);
        setTasks(formattedTasks);
      } else {
        setError(result.error);
        toast.error('Error al cargar las tareas');
      }
    } catch (err) {
      setError(err.message);
      toast.error('Error al cargar las tareas');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTasks([]);
    setError(null);
    setFilterStatus('all');
    setShowCreateModal(false);
    setShowEditModal(false);
    setSelectedTask(null);
    setNewTask({
      name: '',
      description: '',
      initialDate: '',
      finalDate: ''
    });
    setDeleteModal({
      isOpen: false,
      task: null,
      loading: false
    });
    onClose();
  };

  const handleEditTask = (task, e) => {
    e.stopPropagation();
    setSelectedTask(task);
    setShowEditModal(true);
  };

  // Función para manejar clic en la fila de la tarea
  const handleTaskRowClick = (task) => {
    setSelectedTask(task);
    setShowEditModal(true);
  };

  const handleTaskUpdated = () => {
    loadTasks();
    // Notificar al componente padre (Dashboard) que las tareas han sido actualizadas
    if (onTasksUpdated) {
      onTasksUpdated();
    }
  };

  const openDeleteModal = (task, e) => {
    e.stopPropagation();
    setDeleteModal({
      isOpen: true,
      task,
      loading: false
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      task: null,
      loading: false
    });
  };

  const confirmDeleteTask = async () => {
    if (!deleteModal.task || deleteModal.loading) return;

    setDeleteModal(prev => ({ ...prev, loading: true }));
    const loadingToast = toast.loading('Eliminando tarea...');

    try {
      const result = await TaskService.deleteTask(deleteModal.task.id);

      if (result.success) {
        toast.success('Tarea eliminada exitosamente', { id: loadingToast });
        closeDeleteModal();
        loadTasks();
        if (onTasksUpdated) {
          onTasksUpdated();
        }
      } else {
        toast.error(result.error || 'Error al eliminar la tarea', { id: loadingToast });
        setDeleteModal(prev => ({ ...prev, loading: false }));
      }
    } catch (err) {
      toast.error('Error al eliminar la tarea', { id: loadingToast });
      console.error('Error deleting task:', err);
      setDeleteModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    
    // Validar campos
    if (!newTask.name || !newTask.description || !newTask.initialDate || !newTask.finalDate) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    // Validar fechas
    if (!isEndDateAfterStartDate(newTask.initialDate, newTask.finalDate)) {
      toast.error('La fecha de fin debe ser posterior a la fecha de inicio');
      return;
    }

    setCreateLoading(true);
    
    try {
      // Preparar datos para enviar a la API
      const taskData = {
        name: newTask.name,
        description: newTask.description,
        initialDate: formatDateForAPI(newTask.initialDate),
        finalDate: formatDateForAPI(newTask.finalDate),
        projectId: project.id
      };

      const result = await TaskService.createTask(taskData);
      
      if (result.success) {
        toast.success('Tarea creada exitosamente');
        setShowCreateModal(false);
        setNewTask({
          name: '',
          description: '',
          initialDate: '',
          finalDate: ''
        });
        // Recargar las tareas
        loadTasks();
        // Notificar al componente padre (Dashboard) que las tareas han sido actualizadas
        if (onTasksUpdated) {
          onTasksUpdated();
        }
      } else {
        toast.error(result.error || 'Error al crear la tarea');
      }
    } catch (err) {
      toast.error('Error al crear la tarea');
      console.error('Error creating task:', err);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setNewTask(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Filtrar tareas según el estado
  const filteredTasks = tasks.filter(task => 
    filterStatus === 'all' || task.status === filterStatus
  );

  // Estadísticas de tareas
  const taskStats = {
    total: tasks.length,
    pending: tasks.filter(t => t.status === 'pending').length,
    inProgress: tasks.filter(t => t.status === 'in progress').length,
    completed: tasks.filter(t => t.status === 'completed').length,
    cancelled: tasks.filter(t => t.status === 'cancelled').length
  };

  // Obtener icono según el estado
  const getStatusIcon = (status) => {
    const iconMap = {
      'pending': ClockIcon,
      'backlog': ClockIcon,
      'progress': ExclamationTriangleIcon,
      'in progress': ExclamationTriangleIcon,
      'completed': CheckCircleIcon,
      'cancelled': XMarkIcon,
      'blocked': ExclamationTriangleIcon
    };
    return iconMap[status] || ClockIcon;
  };

  // Formatear fecha
  const formatDate = (dateString) => {
    if (!dateString) return 'Sin fecha';
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // Calcular días restantes
  const getDaysRemaining = (finalDate) => {
    if (!finalDate) return null;
    const today = new Date();
    const endDate = new Date(finalDate);
    const diffTime = endDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={handleClose}
            />
            
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-4xl bg-white dark:bg-gray-800 rounded-xl shadow-xl"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 bg-${project?.color || 'blue'}-100 dark:bg-${project?.color || 'blue'}-900 rounded-lg flex items-center justify-center`}>
                    <span className="text-xl">{project?.icon || '📋'}</span>
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {project?.name || 'Proyecto'}
                    </h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {project?.description || 'Descripción del proyecto'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 text-gray-500" />
                </button>
              </div>
              
              {/* Contenido */}
              <div className="p-6">
                {/* Estadísticas */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                  <div className="card p-4 text-center">
                    <div className="text-2xl font-bold text-gray-900 dark:text-white">{taskStats.total}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Total</div>
                  </div>
                  <div className="card p-4 text-center">
                    <div className="text-2xl font-bold text-yellow-600">{taskStats.pending}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Pendientes</div>
                  </div>
                  <div className="card p-4 text-center">
                    <div className="text-2xl font-bold text-green-600">{taskStats.inProgress}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">En Progreso</div>
                  </div>
                  <div className="card p-4 text-center">
                    <div className="text-2xl font-bold text-blue-600">{taskStats.completed}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Completadas</div>
                  </div>
                  <div className="card p-4 text-center">
                    <div className="text-2xl font-bold text-red-600">{taskStats.cancelled}</div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">Canceladas</div>
                  </div>
                </div>

                {/* Filtros */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600 dark:text-gray-400">Filtrar por estado:</span>
                    <select
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="input-field py-1 px-3 text-sm"
                    >
                      <option value="all">Todos</option>
                      <option value="pending">Pendientes</option>
                      <option value="in progress">En Progreso</option>
                      <option value="completed">Completadas</option>
                      <option value="cancelled">Canceladas</option>
                    </select>
                  </div>
                  <button 
                    onClick={() => setShowCreateModal(true)}
                    className="btn-primary flex items-center space-x-2"
                  >
                    <PlusIcon className="h-4 w-4" />
                    <span>Nueva Tarea</span>
                  </button>
                </div>

                {/* Lista de tareas */}
                <div className="space-y-4">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                    </div>
                  ) : error ? (
                    <div className="text-center py-12">
                      <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
                      <p className="text-red-600 dark:text-red-400">{error}</p>
                      <button 
                        onClick={loadTasks}
                        className="btn-primary mt-4"
                      >
                        Reintentar
                      </button>
                    </div>
                  ) : filteredTasks.length > 0 ? (
                    filteredTasks.map((task, index) => {
                      const StatusIcon = getStatusIcon(task.status);
                      const daysRemaining = getDaysRemaining(task.finalDate);
                      
                      return (
                        <motion.div
                          key={task.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="card p-4 hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-all relative cursor-pointer group"
                          onClick={() => handleTaskRowClick(task)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-3 flex-1">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${getStatusColorClasses(task.status)}`}>
                                <StatusIcon className="h-4 w-4" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2 mb-1">
                                  <h3 className="font-semibold text-gray-900 dark:text-white">
                                    {task.name}
                                  </h3>
                                  <PencilIcon className="h-3 w-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                                  {task.description}
                                </p>
                                <div className="flex items-center flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                                  <div className="flex items-center space-x-1">
                                    <CalendarIcon className="h-3 w-3" />
                                    <span>Inicio: {formatDate(task.initialDate)}</span>
                                  </div>
                                  <div className="flex items-center space-x-1">
                                    <CalendarIcon className="h-3 w-3" />
                                    <span>Fin: {formatDate(task.finalDate)}</span>
                                  </div>
                                  {daysRemaining !== null && (
                                    <div className={`flex items-center space-x-1 ${
                                      daysRemaining < 0 ? 'text-red-500' : 
                                      daysRemaining < 3 ? 'text-yellow-500' : 
                                      'text-green-500'
                                    }`}>
                                      <ClockIcon className="h-3 w-3" />
                                      <span>
                                        {daysRemaining < 0 ? `${Math.abs(daysRemaining)} días vencido` :
                                         daysRemaining === 0 ? 'Vence hoy' :
                                         `${daysRemaining} días restantes`}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <div className="flex flex-col items-end space-y-1">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColorClasses(task.status)}`}>
                                  {getStatusLabel(task.status)}
                                </span>
                                <span className="text-xs text-gray-500">
                                  ID: {task.id}
                                </span>
                              </div>
                              {/* Botones de acción */}
                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={(e) => handleEditTask(task, e)}
                                  className="p-1.5 rounded-lg bg-white dark:bg-gray-700 hover:bg-blue-100 dark:hover:bg-blue-600 transition-colors shadow-sm"
                                  title="Editar tarea"
                                >
                                  <PencilIcon className="h-3.5 w-3.5 text-blue-600 dark:text-blue-300" />
                                </button>
                                <button
                                  onClick={(e) => openDeleteModal(task, e)}
                                  className="p-1.5 rounded-lg bg-white dark:bg-gray-700 hover:bg-red-100 dark:hover:bg-red-600 transition-colors shadow-sm"
                                  title="Eliminar tarea"
                                >
                                  <TrashIcon className="h-3.5 w-3.5 text-red-600 dark:text-red-300" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })
                  ) : (
                    <div className="text-center py-12">
                      <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500 dark:text-gray-400">
                        {filterStatus === 'all' ? 'No hay tareas en este proyecto' : `No hay tareas con estado "${filterStatus}"`}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )}
      <AnimatePresence>
        {deleteModal.isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black z-[60]"
              onClick={() => {
                if (!deleteModal.loading) {
                  closeDeleteModal();
                }
              }}
            />
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="card w-full max-w-md p-6"
              >
                <div className="text-3xl mb-4 text-red-500 text-center">🗑️</div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 text-center">
                  ¿Deseas eliminar la tarea?
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 text-center">
                  Esta acción no se puede deshacer y se eliminará la tarea{' '}
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {deleteModal.task?.name}
                  </span>.
                </p>
                <div className="flex items-center justify-end space-x-3">
                  <button
                    type="button"
                    onClick={closeDeleteModal}
                    disabled={deleteModal.loading}
                    className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={confirmDeleteTask}
                    disabled={deleteModal.loading}
                    className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deleteModal.loading ? 'Eliminando...' : 'Eliminar'}
                  </button>
                </div>
              </motion.div>
            </div>
          </>
        )}
      </AnimatePresence>

      {/* Modal de Crear Tarea */}
      {showCreateModal && (
        <div className="fixed inset-0 z-[60] overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={() => setShowCreateModal(false)}
            />
            
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-lg bg-white dark:bg-gray-800 rounded-xl shadow-xl"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Crear Nueva Tarea
                </h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <XMarkIcon className="h-5 w-5 text-gray-500" />
                </button>
              </div>
              
              {/* Formulario */}
              <form onSubmit={handleCreateTask} className="p-6 space-y-4">
                {/* Nombre de la tarea */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Nombre de la Tarea *
                  </label>
                  <input
                    type="text"
                    value={newTask.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className="input-field w-full"
                    placeholder="Ej: Pruebas Cucumber"
                    required
                  />
                </div>

                {/* Descripción */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Descripción *
                  </label>
                  <textarea
                    value={newTask.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    className="input-field w-full"
                    placeholder="Descripción detallada de la tarea"
                    rows="3"
                    required
                  />
                </div>

                {/* Fecha Inicial */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Fecha de Inicio *
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      value={newTask.initialDate}
                      onChange={(e) => handleInputChange('initialDate', e.target.value)}
                      max={getMaxDateString()}
                      className="input-field w-full pr-10"
                      required
                    />
                    <CalendarIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                {/* Fecha Final */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Fecha de Fin *
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      value={newTask.finalDate}
                      onChange={(e) => handleInputChange('finalDate', e.target.value)}
                      min={newTask.initialDate}
                      max={getMaxDateString()}
                      className="input-field w-full pr-10"
                      required
                    />
                    <CalendarIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                {/* Información del Proyecto */}
                <div className="card p-3 bg-blue-50 dark:bg-blue-900/20">
                  <div className="flex items-center space-x-2 text-sm text-blue-700 dark:text-blue-300">
                    <span className="font-medium">Proyecto:</span>
                    <span>{project?.name || 'N/A'}</span>
                    <span className="text-xs text-gray-500">(ID: {project?.id})</span>
                  </div>
                </div>

                {/* Botones */}
                <div className="flex items-center justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={createLoading}
                    className="btn-primary flex items-center space-x-2"
                  >
                    {createLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Creando...</span>
                      </>
                    ) : (
                      <>
                        <PlusIcon className="h-4 w-4" />
                        <span>Crear Tarea</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      )}

      {/* Modal de Editar Tarea */}
      <EditTaskModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        task={selectedTask}
        onTaskUpdated={handleTaskUpdated}
      />
    </AnimatePresence>
  );
};

export default ProjectTasksModal;

